import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, Filter } from "lucide-react";
import { useDealTracker } from "@/hooks/useDealTracker";
import { DealTrackerLead, DEAL_STAGES, DealStage } from "@/types/dealTracker";
import NewLeadModal, { NewLeadFormData } from "@/components/seller/dealTracker/NewLeadModal";
import LeadDetailModal from "@/components/seller/dealTracker/LeadDetailModal";
import { format } from "date-fns";
import TourWrapper from "@/components/onboarding/TourWrapper";
import { sellerDealTrackerSteps } from "@/config/tours";
import { safeStorage } from "@/utils/safariStorage";

const TOUR_KEY = 'godyspo_tour_seller_deal_tracker';

const DealTrackerPage = () => {
  const location = useLocation();
  const { 
    leads, 
    isLoading, 
    stageCounts, 
    createLead, 
    updateLead, 
    updateStage, 
    deleteLead 
  } = useDealTracker();
  
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<DealTrackerLead | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<DealStage | 'all'>('all');
  const [runTour, setRunTour] = useState(false);
  
  // Tour initialization
  useEffect(() => {
    const startTour = (location.state as { startTour?: boolean })?.startTour;
    const hasSeenTour = safeStorage.getItem(TOUR_KEY) === 'completed';
    
    if (startTour || !hasSeenTour) {
      // Poll for elements with delay
      let attempts = 0;
      const maxAttempts = 5;
      
      const checkElements = () => {
        const header = document.querySelector('[data-tour="deal-tracker-header"]');
        if (header || attempts >= maxAttempts) {
          setTimeout(() => setRunTour(true), 500);
        } else {
          attempts++;
          setTimeout(checkElements, 500);
        }
      };
      
      setTimeout(checkElements, 300);
    }
  }, [location.state]);
  
  const handleTourComplete = () => {
    setRunTour(false);
    safeStorage.setItem(TOUR_KEY, 'completed');
  };
  
  const handleTourSkip = () => {
    setRunTour(false);
    safeStorage.setItem(TOUR_KEY, 'completed');
  };
  
  // Keep selectedLead in sync with leads data after mutations
  useEffect(() => {
    if (selectedLead) {
      const updatedLead = leads.find(l => l.id === selectedLead.id);
      if (updatedLead) {
        setSelectedLead(updatedLead);
      }
    }
  }, [leads, selectedLead]);
  
  // Filter leads based on search and stage
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch = !searchQuery || 
        lead.seller_names.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.property_addresses?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStage = stageFilter === 'all' || lead.deal_stage === stageFilter;
      
      return matchesSearch && matchesStage;
    });
  }, [leads, searchQuery, stageFilter]);
  
  const handleCreateLead = async (data: NewLeadFormData) => {
    await createLead.mutateAsync(data);
    setShowNewLeadModal(false);
  };
  
  const handleUpdateLead = async (data: Partial<DealTrackerLead> & { id: string }) => {
    await updateLead.mutateAsync(data);
  };
  
  const handleStageChange = async (id: string, stage: DealStage) => {
    await updateStage.mutateAsync({ id, deal_stage: stage });
  };
  
  const handleDeleteLead = async (id: string) => {
    await deleteLead.mutateAsync(id);
    setSelectedLead(null);
  };
  
  const getStageInfo = (stage: DealStage) => {
    return DEAL_STAGES.find(s => s.value === stage);
  };
  
  return (
    <div className="space-y-6">
      <TourWrapper
        steps={sellerDealTrackerSteps}
        run={runTour}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
      />
      
      {/* Header */}
      <div 
        data-tour="deal-tracker-header"
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold">Deal Tracker</h1>
          <p className="text-muted-foreground">Track your deals from agreement to closing</p>
        </div>
        <Button onClick={() => setShowNewLeadModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Deal
        </Button>
      </div>
      
      {/* Stage Stats */}
      <div 
        data-tour="deal-tracker-stages"
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
      >
        {DEAL_STAGES.map((stage) => (
          <Card 
            key={stage.value}
            className={`cursor-pointer transition-all hover:border-primary ${
              stageFilter === stage.value ? 'border-primary ring-1 ring-primary' : ''
            }`}
            onClick={() => setStageFilter(stageFilter === stage.value ? 'all' : stage.value)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{stageCounts[stage.value] || 0}</span>
                <div className={`w-3 h-3 rounded-full ${stage.color}`} />
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{stage.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Filters */}
      <div 
        data-tour="deal-tracker-filters"
        className="flex flex-col md:flex-row gap-3"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, property, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={stageFilter} onValueChange={(v) => setStageFilter(v as DealStage | 'all')}>
          <SelectTrigger className="w-full md:w-[200px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {DEAL_STAGES.map((stage) => (
              <SelectItem key={stage.value} value={stage.value}>
                {stage.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Leads Table */}
      <Card data-tour="deal-tracker-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Seller Name</TableHead>
                <TableHead className="hidden md:table-cell">Property Address</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead className="hidden md:table-cell">Deal Source</TableHead>
                <TableHead className="hidden md:table-cell">Commission</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-5 w-48" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell className="hidden lg:table-cell"><Skeleton className="h-5 w-24" /></TableCell>
                  </TableRow>
                ))
              ) : filteredLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="text-muted-foreground">
                      {leads.length === 0 ? (
                        <>
                          <p className="mb-2">No deals yet</p>
                          <Button variant="outline" onClick={() => setShowNewLeadModal(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add your first deal
                          </Button>
                        </>
                      ) : (
                        <p>No deals match your filters</p>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLeads.map((lead) => {
                  const stageInfo = getStageInfo(lead.deal_stage);
                  return (
                    <TableRow 
                      key={lead.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <TableCell className="font-medium">{lead.seller_names}</TableCell>
                      <TableCell className="hidden md:table-cell max-w-[200px] truncate">
                        {lead.property_addresses || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={stageInfo?.color}>
                          {stageInfo?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{lead.lead_source}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {lead.expected_commission 
                          ? `$${Number(lead.expected_commission.replace(/[^0-9.-]+/g, '')).toLocaleString()}`
                          : '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {format(new Date(lead.created_at), 'MMM d, yyyy')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      
      {/* New Lead Modal */}
      <NewLeadModal
        open={showNewLeadModal}
        onOpenChange={setShowNewLeadModal}
        onSubmit={handleCreateLead}
        isLoading={createLead.isPending}
      />
      
      {/* Lead Detail Modal */}
      <LeadDetailModal
        lead={selectedLead}
        open={!!selectedLead}
        onOpenChange={(open) => !open && setSelectedLead(null)}
        onUpdate={handleUpdateLead}
        onStageChange={handleStageChange}
        onDelete={handleDeleteLead}
        isUpdating={updateLead.isPending || updateStage.isPending}
        isDeleting={deleteLead.isPending}
      />
    </div>
  );
};

export default DealTrackerPage;
